import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { workforceApi } from "../../lib/workforce";
import { Users2, Plus, Crown, X, ArrowLeft, Trash2 } from "lucide-react";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [t, w] = await Promise.all([
      api.get("/workforce/teams").then((r) => r.data),
      workforceApi.listWorkers(),
    ]);
    setTeams(t.teams || []);
    setWorkers(w.workers || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="teams-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users2 size={20} strokeWidth={2.5} />
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Teams</div>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">Group your workforce</h1>
        </div>
        <button onClick={() => setCreating(true)} className="brut-btn" data-testid="create-team-btn">
          <Plus size={16} strokeWidth={2.5} /> New team
        </button>
      </div>

      {teams.length === 0 ? (
        <div className="brut-card p-8 text-center">
          <div className="text-5xl mb-3 float-y inline-block">🧑‍🤝‍🧑</div>
          <div className="font-heading text-lg font-bold mb-1">No teams yet</div>
          <p className="text-sm text-slate-600 mb-4">Group workers under a leader with shared instructions and memory.</p>
          <button onClick={() => setCreating(true)} className="brut-btn"><Plus size={14} strokeWidth={2.5} /> Create your first team</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-list">
          {teams.map((t) => (
            <TeamCard key={t.team_id} team={t} workers={workers} onReload={load} />
          ))}
        </div>
      )}

      {creating && (
        <CreateTeamModal
          workers={workers}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function TeamCard({ team, workers, onReload }) {
  const [open, setOpen] = useState(false);
  const [mem, setMem] = useState("");
  const workerMap = Object.fromEntries(workers.map((w) => [w.worker_id, w]));
  const leader = workerMap[team.leader_worker_id];
  const members = (team.member_worker_ids || []).map((id) => workerMap[id]).filter(Boolean);

  const addMem = async () => {
    if (!mem.trim()) return;
    await api.post(`/workforce/teams/${team.team_id}/memory`, { content: mem });
    setMem(""); onReload();
  };
  const delMem = async (mid) => {
    await api.delete(`/workforce/teams/${team.team_id}/memory/${mid}`);
    onReload();
  };
  const delTeam = async () => {
    if (!window.confirm(`Delete "${team.name}"? Workers won't be deleted.`)) return;
    await api.delete(`/workforce/teams/${team.team_id}`);
    onReload();
  };

  return (
    <div className="brut-card p-4" data-testid={`team-${team.team_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-heading font-bold">{team.name}</div>
          <div className="text-xs text-slate-500">{team.description || "—"}</div>
        </div>
        <button onClick={delTeam} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
      </div>

      {leader && (
        <div className="flex items-center gap-2 text-sm mb-2 p-2 bg-amber-50 border-2 border-slate-900 rounded-lg">
          <Crown size={14} className="text-amber-600" />
          <span className="text-xl">{leader.avatar}</span>
          <div>
            <div className="font-bold">{leader.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Leader</div>
          </div>
        </div>
      )}

      {members.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Members</div>
          <div className="flex flex-wrap gap-1.5">
            {members.map((m) => (
              <Link key={m.worker_id} to={`/workforce/worker/${m.worker_id}`}
                className="flex items-center gap-1 px-2 py-1 rounded-full border-2 border-slate-300 hover:border-slate-900 text-xs">
                <span>{m.avatar}</span> {m.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setOpen(!open)} className="text-xs font-bold text-slate-600 hover:text-slate-900 mt-2"
              data-testid={`team-expand-${team.team_id}`}>
        {open ? "Hide" : "Show"} shared memory ({(team.shared_memory || []).length})
      </button>

      {open && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-2">
          {(team.shared_memory || []).map((m) => (
            <div key={m.memory_id} className="flex items-start gap-2 text-xs bg-white border border-slate-200 rounded p-2">
              <span className="flex-1">{m.content}</span>
              <button onClick={() => delMem(m.memory_id)}><X size={11} className="text-slate-400 hover:text-red-500" /></button>
            </div>
          ))}
          <div className="flex gap-1">
            <input value={mem} onChange={(e) => setMem(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMem(); } }}
                   placeholder="Add team memory…" className="brut-input py-1.5 text-xs flex-1" />
            <button onClick={addMem} className="brut-btn brut-btn-outline text-xs px-2 py-1.5"><Plus size={12} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTeamModal({ workers, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [leader, setLeader] = useState("");
  const [members, setMembers] = useState([]);
  const [inst, setInst] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setMembers((m) => m.includes(id) ? m.filter((x) => x !== id) : [...m, id]);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/workforce/teams", {
        name, description: desc,
        leader_worker_id: leader || null,
        member_worker_ids: members.filter((m) => m !== leader),
        shared_instructions: inst,
      });
      onCreated();
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-30 bg-slate-900/40" onClick={onClose} />
      <div className="fixed z-40 inset-x-4 top-16 max-w-lg mx-auto brut-card p-6 max-h-[85vh] overflow-y-auto" data-testid="create-team-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold">New team</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Team name (e.g., Strategy Squad)" className="brut-input" data-testid="team-name" autoFocus />
          <input value={desc} onChange={(e) => setDesc(e.target.value)}
                 placeholder="What does this team do?" className="brut-input" data-testid="team-desc" />
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Leader</label>
            <select value={leader} onChange={(e) => setLeader(e.target.value)} className="brut-input" data-testid="team-leader">
              <option value="">— No leader —</option>
              {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{w.avatar} {w.name} · {w.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Members</label>
            <div className="max-h-52 overflow-y-auto space-y-1 border-2 border-slate-300 rounded-xl p-2" data-testid="team-members">
              {workers.filter((w) => w.worker_id !== leader).map((w) => (
                <label key={w.worker_id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                  <input type="checkbox" checked={members.includes(w.worker_id)} onChange={() => toggle(w.worker_id)}
                         className="accent-slate-900" data-testid={`member-${w.worker_id}`} />
                  <span className="text-lg">{w.avatar}</span>
                  <span className="text-sm font-semibold">{w.name}</span>
                  <span className="text-xs text-slate-500">· {w.title}</span>
                </label>
              ))}
            </div>
          </div>
          <textarea value={inst} onChange={(e) => setInst(e.target.value)}
                    placeholder="Shared team instructions (optional)" rows={3}
                    className="brut-input resize-none" data-testid="team-instructions" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="brut-btn brut-btn-outline text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()} className="brut-btn text-sm" data-testid="team-submit">
            {busy ? "Creating…" : "Create team"}
          </button>
        </div>
      </div>
    </>
  );
}
